import getContext from '../../utils/context/getContext';
import {
  ExtensionType,
  SharedExtensionDefinition,
} from '../types/ExtensionCard';
import { GameStage } from '../types/roomState';
import { declOfNum } from '../utils/declOfNum';
import { isFull } from '../utils/isFull';
import { getWorkingExtensions } from '../utils/getWorkingExtensions';

type PartialSharedExtensionDefinition = Omit<SharedExtensionDefinition, 'type'>;

const sharedExtensions: Record<
  ExtensionType,
  PartialSharedExtensionDefinition
> = {
  [ExtensionType.predator]: {
    additionalFood: 1.3,
    chanceModifier: 1,
    canBeInherited: true,
    conflictsWith: [ExtensionType.chlorophyll],
    meta: {
      name: 'Predator',
      description: 'Can attack other animals',
    },
  },
  [ExtensionType.chlorophyll]: {
    additionalFood: -1,
    chanceModifier: 0.6,
    canBeInherited: true,
    conflictsWith: [
      ExtensionType.predator,
      ExtensionType.communication,
      ExtensionType.cooperation,
    ],
    meta: {
      name: 'Photosynthesis',
      description:
        'Needs less food. Cannot be a predator and cannot cooperate.',
    },
  },
  [ExtensionType.aedificator]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    canBeInherited: true,
    meta: {
      name: 'Aedificator',
      shortName: 'Aed.',
      description: 'Adds two food to the food base at the start of the turn',
    },
  },
  [ExtensionType.horns]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    canBeInherited: true,
    meta: {
      name: 'Horns',
      description: 'After an attack, has a 60% chance to kill the predator',
    },
  },
  [ExtensionType.transparent]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    conflictsWith: [ExtensionType.burrowing],
    canBeInherited: true,
    canBeAttacked: ({ animal }) => animal.satiety !== 0,
    meta: {
      name: 'Transparency',
      shortName: 'Transp.',
      description: 'Cannot be attacked before it has eaten',
    },
  },
  [ExtensionType.camouflage]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    canBeInherited: true,
    canBeAttacked: (_context, predator) =>
      getWorkingExtensions(predator).some(
        (extension) => `${extension.type}` === `${ExtensionType.sharpVision}`,
      ),
    meta: {
      name: 'Camouflage',
      description: 'Can only be attacked by a predator with sharp vision',
    },
  },
  [ExtensionType.sharpVision]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    canBeInherited: true,
    meta: {
      name: 'Sharp Vision',
      shortName: 'Sharp V.',
      description: 'Can attack animals with camouflage',
    },
  },
  [ExtensionType.big]: {
    additionalFood: 1,
    chanceModifier: 0.5,
    canBeInherited: true,
    canBeAttacked: (_context, predator) =>
      getWorkingExtensions(predator).some(
        (extension) => extension.type === ExtensionType.big,
      ),
    meta: {
      name: 'Large',
      description: 'Can only be attacked by a large predator',
    },
  },
  [ExtensionType.burrowing]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    canBeInherited: true,
    conflictsWith: [ExtensionType.transparent],
    canBeAttacked: (context) => !isFull(context),
    getAdditionalMeta: (context) => (isFull(context) ? 'in burrow' : null),
    meta: {
      name: 'Burrowing',
      description: 'When fed, cannot be attacked by a predator',
    },
  },
  [ExtensionType.crazing]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    canBeUsedOnStages: [GameStage.eating],
    canBeUsed: ({ room }) => !!room.gameState.foodOnTheTable,
    meta: {
      name: 'Trampling',
      description: 'When used, destroys one food from the food base',
    },
  },
  [ExtensionType.hibernation]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    canBeInherited: true,
    canBeUsedOnStages: [GameStage.eating],
    canBeUsedOnceInStage: true,
    canNotBeUsedInLastStep: true,
    meta: {
      name: 'Hibernation',
      description:
        'After use, counts as fed. Cannot be used two turns in a row.',
    },
  },
  [ExtensionType.poisonous]: {
    additionalFood: 0,
    canBeInherited: true,
    chanceModifier: 0.2,
    meta: {
      name: 'Poisonous',
      shortName: 'Pois.',
      description: 'The predator that eats this animal dies during extinction',
    },
  },
  [ExtensionType.running]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    canBeInherited: true,
    meta: {
      name: 'Running',
      description:
        'When attacked, has a chance not to be eaten (50% against a normal predator, 10% against a running one)',
    },
  },
  [ExtensionType.scavenger]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    canBeInherited: true,
    meta: {
      name: 'Scavenger',
      description: 'Gets one food when another animal is eaten',
    },
  },
  [ExtensionType.swimming]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    canBeInherited: true,
    canBeAttacked: (_context, predator) =>
      getWorkingExtensions(predator).some(
        (extension) => extension.type === ExtensionType.swimming,
      ),
    canAttack: (_predator, prey) =>
      getWorkingExtensions(prey).some(
        (extension) => extension.type === ExtensionType.swimming,
      ),
    meta: {
      name: 'Swimming',
      shortName: 'Swim.',
      description: 'Can only be attacked by a swimming predator',
    },
  },
  [ExtensionType.tailLoss]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    canBeInherited: true,
    meta: {
      name: 'Tail Loss',
      shortName: 'Tail L.',
      description:
        'When attacked, discards this or another random evolution card',
    },
  },
  [ExtensionType.piracy]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    canBeUsed: ({ room, self }) => {
      const fatFull = isFull(getContext(room, { animal: self }), true);
      if (fatFull || self.cantEat) {
        return false;
      }

      const availableAnimals = room.players
        .filter((player) => player.id !== room.gameState.activeUser)
        .flatMap((player) =>
          player.cards.filter(
            (card) =>
              !isFull(getContext(room, { animal: card })) && card.satiety > 0,
          ),
        );

      return availableAnimals.length > 0;
    },
    canBeUsedOnStages: [GameStage.eating],
    meta: {
      name: 'Piracy',
      description: 'Take food from another unfed animal',
    },
  },
  [ExtensionType.parasite]: {
    additionalFood: 2,
    chanceModifier: 0.3,
    canBeAddedToEnemyAnimal: true,
    conflictsWith: [ExtensionType.immunity],
    meta: {
      name: 'Parasite',
      description: "Can be added to another player's animal",
    },
  },
  [ExtensionType.scream]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    meta: {
      name: 'Scream',
      description: 'Warns adjacent animals when attacked',
    },
  },
  [ExtensionType.spikes]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    canBeInherited: true,
    meta: {
      name: 'Spikes',
      description:
        'After attacking this animal, the predator cannot attack again this turn',
    },
  },
  [ExtensionType.parasitePair]: {
    additionalFood: 1,
    chanceModifier: 0.3,
    canBeAddedToEnemyAnimal: true,
    shouldBePaired: true,
    conflictsWith: [ExtensionType.immunity],
    meta: {
      name: 'Trematode',
      description: "Can be added to a pair of another player's animals",
    },
  },
  [ExtensionType.fat]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    canBeInherited: true,
    inheritChance: 0.5,
    optionalAdditionalFood: 1,
    canBeAddeddMultipleTimes: true,
    canBeUsedOnceInStage: true,
    canBeUsedOnStages: [GameStage.eating],
    canBeUsed: ({ room, self, extension }) =>
      !!extension.data?.storedFood ||
      isFull(getContext(room, { animal: self })),
    meta: {
      name: 'Fat Reserve',
      shortName: 'Fat Res.',
      description: 'Consumes stored energy for feeding when used',
    },
  },
  [ExtensionType.cooperation]: {
    additionalFood: 0,
    shouldBePaired: true,
    chanceModifier: 0.3,
    conflictsWith: [ExtensionType.chlorophyll],
    meta: {
      name: 'Cooperation',
      shortName: 'Coop.',
      description:
        'When one animal in the pair gets food, the other gets food too',
    },
  },
  [ExtensionType.communication]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    shouldBePaired: true,
    conflictsWith: [ExtensionType.chlorophyll],
    meta: {
      name: 'Communication',
      shortName: 'Comm.',
      description:
        'When one animal in the pair eats from the food base, the other gets food out of turn',
    },
  },
  [ExtensionType.fly]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    canBeInherited: true,
    getAdditionalMeta: ({ animal }) => `${animal.extensions.length} kg`,
    canBeAttacked: ({ animal }, predator) =>
      animal.extensions.length > predator.extensions.length,
    meta: {
      name: 'Flight',
      description: 'Cannot be attacked by a "heavier" predator',
    },
  },
  [ExtensionType.angler]: {
    additionalFood: 0,
    chanceModifier: 0.2,
    isHidden: true,
    meta: {
      name: 'Angler',
      description:
        'While undisclosed and the only evolution card, counterattacks the predator.',
    },
  },
  [ExtensionType.pseudoPoisonous]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    canBeInherited: true,
    conflictsWith: [ExtensionType.poisonous],
    mimics: ExtensionType.poisonous,
    meta: {
      name: 'Bright Coloration',
      shortName: 'Bright.',
      description: 'Looks like Poisonous. So does the card.',
    },
  },
  [ExtensionType.egg]: {
    additionalFood: 0,
    chanceModifier: 0,
    isBlockingEverything: true,
    getAdditionalMeta: ({ animal }) => {
      const eggExtension = animal.extensions.find(
        (extension) => extension.type === ExtensionType.egg,
      );
      if (eggExtension?.data?.stepsToBeBorn) {
        return `hatches in ${eggExtension.data.stepsToBeBorn} ${declOfNum(
          eggExtension.data.stepsToBeBorn,
          ['turn', 'turns', 'turns'],
        )}`;
      }
      return '';
    },
    meta: {
      name: 'Egg',
      description: 'This animal has not hatched yet. It cannot do anything.',
    },
  },
  [ExtensionType.layingEggs]: {
    additionalFood: 0,
    chanceModifier: 0.12,
    canBeInherited: true,
    inheritChance: 0.1,
    conflictsWith: [ExtensionType.viviparous],
    meta: {
      name: 'Egg Laying',
      shortName: 'Egg L.',
      description: 'When fed, lays several eggs. They hatch after a few turns',
    },
  },
  [ExtensionType.viviparous]: {
    additionalFood: 0,
    chanceModifier: 0.3,
    canBeInherited: true,
    inheritChance: 0.1,
    conflictsWith: [ExtensionType.layingEggs],
    meta: {
      name: 'Viviparity',
      shortName: 'Viv.',
      description:
        'When fed, gives birth to a young animal. The offspring may inherit evolution cards.',
    },
  },
  [ExtensionType.ink]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    canBeInherited: true,
    meta: {
      name: 'Ink Cloud',
      shortName: 'Ink Cl.',
      description: 'Interrupts an attack on itself once per turn',
    },
  },
  [ExtensionType.patron]: {
    additionalFood: 0,
    chanceModifier: 0.5,
    meta: {
      name: 'Patron',
      description: 'Protects adjacent animals without evolution cards',
    },
  },
  [ExtensionType.immunity]: {
    additionalFood: 1,
    chanceModifier: 0.5,
    canBeInherited: true,
    meta: {
      name: 'Immunity',
      description:
        'Parasites cannot be placed on this animal. Existing parasites are removed',
    },
  },
  [ExtensionType.intellect]: {
    additionalFood: 1,
    additionalFoodLabel: '+1/3/5...',
    customAdditionalFood: (context) => {
      const intellectExtensions = context.animal.extensions.filter(
        (extension) => extension.type === ExtensionType.intellect,
      );
      const selfIndex = intellectExtensions.indexOf(context.extension);
      return 1 + selfIndex * 2;
    },
    chanceModifier: 0.5,
    canBeInherited: true,
    inheritChance: 0.5,
    canBeAddeddMultipleTimes: true,
    meta: {
      name: 'Intellect',
      shortName: '🧠',
      description: 'Once per turn ignores one defense card of another animal',
    },
  },
  [ExtensionType.tumor]: {
    conflictsWith: [ExtensionType.immunity],
    additionalFood: 0,
    chanceModifier: 0.2,
    canBeAddedToEnemyAnimal: true,
    meta: {
      name: 'Tumor',
      description: 'Gradually kills the animal',
    },
  },
  [ExtensionType.shell]: {
    additionalFood: 0,
    chanceModifier: 0.1,
    meta: {
      name: 'Shell',
      description:
        'During an attack, hides in its shell and cannot eat until the end of the turn.',
    },
  },
};

export const sharedExtensionCards = Object.fromEntries(
  Object.entries(sharedExtensions).map(([key, value]) => [
    key,
    { ...value, type: key },
  ]),
) as unknown as Record<ExtensionType, SharedExtensionDefinition>;

export type SharedExtensionCards = typeof sharedExtensionCards;
